import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";

// Types matching the .NET API DTOs
interface TemplateListDto {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  thumbnail: string | null;
  isPublic: boolean;
  isSystem: boolean;
  usageCount: number;
  userId: string | null;
  userName: string | null;
  createdAt: string;
}

interface TemplateDto extends TemplateListDto {
  content: unknown;
  updatedAt: string;
}

interface DocumentDto {
  id: string;
  title: string;
  ownerId: string;
  teamId: string | null;
  language: string;
  paperSize: string;
  fontFamily: string;
  fontSize: number;
  isPublic: boolean;
  shareLink: string | null;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt: string | null;
  blocks: unknown[];
  bibliography: unknown[];
  labels: unknown[];
}

interface UseTemplatesParams {
  category?: string;
  enabled?: boolean;
}

export function useTemplates(params?: UseTemplatesParams) {
  const { fetchWithAuth } = useApiContext();

  const { data, isLoading, error } = useQuery({
    queryKey: ["templates", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.category) searchParams.set("category", params.category);

      const response = await fetchWithAuth(
        `${API_BASE}/templates?${searchParams.toString()}`
      );
      // .NET API returns array directly
      return handleResponse<TemplateListDto[]>(response);
    },
    enabled: params?.enabled ?? true,
  });

  return {
    templates: data,
    isLoading,
    error,
  };
}

export function useTemplate(id: string) {
  const { fetchWithAuth } = useApiContext();

  const { data, isLoading, error } = useQuery({
    queryKey: ["template", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/templates/${id}`);
      // .NET API returns object directly
      return handleResponse<TemplateDto>(response);
    },
    enabled: !!id,
  });

  return {
    template: data,
    isLoading,
    error,
  };
}

interface CreateTemplateInput {
  documentId: string;
  name: string;
  description?: string;
  category?: string;
  isPublic: boolean;
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: CreateTemplateInput) => {
      const response = await fetchWithAuth(`${API_BASE}/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      // .NET API returns object directly
      return handleResponse<TemplateDto>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}

export function useUseTemplate() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async ({ templateId, title }: { templateId: string; title?: string }) => {
      const response = await fetchWithAuth(`${API_BASE}/templates/${templateId}/use`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      // .NET API returns DocumentDto directly
      return handleResponse<DocumentDto>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/templates/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete template");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
}
